import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { AppState, AppAction } from '@/types';
import { SubscriptionTier } from '@everyday-os/shared';
import { DowngradeWarningModal } from '@/components/ui/DowngradeWarningModal';
import { showToast } from '@/components/ui/Toast';

const RECAPTCHA_SITE_KEY = '6LcVLIYsAAAAADaw3VMLLs4jXulXVwzwsp3qdex6';

async function getRecaptchaToken(action: string): Promise<string> {
  return new Promise((resolve, reject) => {
    window.grecaptcha.enterprise.ready(async () => {
      try {
        const token = await window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, { action });
        resolve(token);
      } catch (e) {
        reject(e);
      }
    });
  });
}

interface UpgradeProps {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

interface PlanConfig {
  tier: SubscriptionTier;
  name: string;
  price: string;
  period: string;
  features: string[];
  priceId: string | null;
  highlighted?: boolean;
}

const TIER_ORDER: Record<string, number> = { FREE: 0, BASIC: 1, PREMIUM: 2 };

const PLANS_STATIC = [
  { tier: SubscriptionTier.FREE,    price: '$0',   priceId: null },
  { tier: SubscriptionTier.BASIC,   price: '$4.99', priceId: import.meta.env.VITE_STRIPE_BASIC_PRICE_ID ?? '' },
  { tier: SubscriptionTier.PREMIUM, price: '$9.99', priceId: import.meta.env.VITE_STRIPE_PREMIUM_PRICE_ID ?? '', highlighted: true },
];

export const Upgrade: React.FC<UpgradeProps> = function Upgrade({ state }: UpgradeProps) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [isManaging, setIsManaging] = useState(false);
  const [upgradeError, setUpgradeError] = useState<string | null>(null);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);

  // Handle return from Stripe portal
  useEffect(() => {
    if (searchParams.get('managed') === 'true') {
      showToast('Your subscription has been updated. Changes may take a moment to reflect.', 'success');
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const PLANS: PlanConfig[] = [
    {
      ...PLANS_STATIC[0],
      name: t('upgrade.plan_free_name', 'Free'),
      period: t('upgrade.plan_free_period', 'forever'),
      features: [
        t('upgrade.plan_free_f1', 'Dashboard'),
        t('upgrade.plan_free_f2', 'Tasks (up to 20)'),
        t('upgrade.plan_free_f3', 'Grocery list'),
        t('upgrade.plan_free_f4', 'Calendar view'),
        t('upgrade.plan_free_f5', 'Up to 2 household members'),
      ],
    },
    {
      ...PLANS_STATIC[1],
      name: t('upgrade.plan_basic_name', 'Basic'),
      period: t('upgrade.plan_period_monthly', 'per month'),
      features: [
        t('upgrade.plan_basic_f1', 'Everything in Free'),
        t('upgrade.plan_basic_f2', 'Unlimited tasks'),
        t('upgrade.plan_basic_f3', 'Contacts management'),
        t('upgrade.plan_basic_f4', 'Maintenance tracking'),
        t('upgrade.plan_basic_f5', 'Up to 5 members'),
      ],
    },
    {
      ...PLANS_STATIC[2],
      name: t('upgrade.plan_premium_name', 'Premium'),
      period: t('upgrade.plan_period_monthly', 'per month'),
      features: [
        t('upgrade.plan_premium_f1', 'Everything in Basic'),
        t('upgrade.plan_premium_f2', 'Meal Planner'),
        t('upgrade.plan_premium_f3', 'Document Vault'),
        t('upgrade.plan_premium_f4', 'AI Assistant'),
        t('upgrade.plan_premium_f5', 'Unlimited members'),
        t('upgrade.plan_premium_f6', 'Priority support'),
      ],
    },
  ];

  const currentHousehold = state.households?.find(h => h.id === state.currentHouseholdId);
  const currentTier = currentHousehold?.subscription?.tier ?? SubscriptionTier.FREE;
  const householdId = currentHousehold?.id;

  async function handleUpgrade(priceId: string) {
    if (!priceId || !householdId) return;
    setIsUpgrading(true);
    setUpgradeError(null);
    try {
      const recaptchaToken = await getRecaptchaToken('checkout');
      const functions = getFunctions(getApp());
      const createSession = httpsCallable(functions, 'createCheckoutSession');
      const result = await createSession({ priceId, householdId, recaptchaToken });
      const { url } = result.data as { url: string };
      window.location.href = url;
    } catch {
      setUpgradeError(t('upgrade.checkout_error', 'Failed to start checkout. Please try again.'));
      setIsUpgrading(false);
    }
  }

  async function handleManageSubscription() {
    setIsManaging(true);
    setUpgradeError(null);
    try {
      const functions = getFunctions(getApp());
      const createPortal = httpsCallable(functions, 'createPortalSession');
      const result = await createPortal({});
      const { url } = result.data as { url: string };
      window.location.href = url;
    } catch {
      setUpgradeError(t('upgrade.portal_error', 'Failed to open billing portal. Please try again.'));
      setIsManaging(false);
    }
  }

  function getPlanButtonLabel(plan: PlanConfig): string {
    const current = TIER_ORDER[currentTier.toUpperCase()] ?? 0;
    const target = TIER_ORDER[plan.tier.toUpperCase()] ?? 0;
    if (current === target) return t('upgrade.current_plan', 'Current Plan');
    if (target > current) return isUpgrading ? t('upgrade.redirecting', 'Redirecting...') : `Upgrade to ${plan.name}`;
    if (plan.tier === SubscriptionTier.FREE) return 'Cancel to Free';
    return `Downgrade to ${plan.name}`;
  }

  function handlePlanClick(plan: PlanConfig) {
    const current = TIER_ORDER[currentTier.toUpperCase()] ?? 0;
    const target = TIER_ORDER[plan.tier.toUpperCase()] ?? 0;
    if (current === target) return;
    if (target > current && plan.priceId) {
      handleUpgrade(plan.priceId);
    } else {
      // Downgrade or cancel — show warning modal first
      setShowDowngradeModal(true);
    }
  }

  return (
    <div className="min-h-full bg-white dark:bg-slate-900 p-6">
      <div className="max-w-page-wide mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-slate-500 dark:text-slate-400 mt-2">
            {t('upgrade.subtitle', 'Choose the plan that works best for your household')}
          </p>
        </div>

        {/* Manage subscription button — shown for paid users */}
        {currentTier !== SubscriptionTier.FREE && (
          <div className="flex justify-center mb-6">
            <button
              onClick={() => setShowDowngradeModal(true)}
              disabled={isManaging}
              className="px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
            >
              {isManaging ? t('upgrade.opening_portal', 'Opening portal...') : 'Manage / Cancel Plan'}
            </button>
          </div>
        )}

        {/* Error */}
        {upgradeError && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm text-center">
            {upgradeError}
          </div>
        )}

        {/* Plans grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {PLANS.map(plan => {
            const current = TIER_ORDER[currentTier.toUpperCase()] ?? 0;
            const target = TIER_ORDER[plan.tier.toUpperCase()] ?? 0;
            const isCurrent = current === target;
            const isDowngrade = target < current;

            return (
              <div
                key={plan.tier}
                className={`relative rounded-2xl p-6 border flex flex-col ${
                  plan.highlighted
                    ? 'border-blue-500 ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-950/30 scale-105'
                    : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-brand text-white text-xs font-semibold rounded-full">
                    {t('upgrade.most_popular', 'Most Popular')}
                  </div>
                )}

                <div className="mb-4">
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">{plan.name}</h2>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-3xl font-black text-slate-900 dark:text-white">{plan.price}</span>
                    <span className="text-slate-500 dark:text-slate-400 text-sm">/ {plan.period}</span>
                  </div>
                </div>

                <ul className="space-y-2 flex-1 mb-6">
                  {plan.features.map(feature => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                      <span className="text-green-500 font-bold">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  disabled={isCurrent || (isUpgrading && !isDowngrade)}
                  onClick={() => handlePlanClick(plan)}
                  className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                    isCurrent
                      ? 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-default'
                      : isDowngrade
                      ? 'border border-amber-400 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/20'
                      : plan.highlighted
                      ? 'bg-brand hover:bg-blue-500 text-white'
                      : 'border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                  } disabled:opacity-60`}
                >
                  {getPlanButtonLabel(plan)}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {showDowngradeModal && (
        <DowngradeWarningModal
          currentTier={currentTier}
          isLoading={isManaging}
          onConfirm={() => {
            setShowDowngradeModal(false);
            handleManageSubscription();
          }}
          onCancel={() => setShowDowngradeModal(false)}
        />
      )}
    </div>
  );
};
